// src/pages/user/QuoteManagement.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FileText, RefreshCw, CheckCircle, Clock, XCircle, Send,
  ThumbsUp, ThumbsDown, Eye, Download, Calendar, User,
  DollarSign, Save, AlertCircle, Trash2, Edit3,
} from "lucide-react";
import { getUserType, USER_TYPES } from "../../config/roles";
import { useAuth } from "../../auth/AuthContext";
import FeatureGate from "../../components/FeatureGate";
import FeatureGateButton from "../../components/FeatureGateButton";
import { useEditorLink } from "../../utils/useEditorLink";
import { injectUserStyles, statusBadgeClass, fmtStatus } from "./userStyles";

const STYLE_ID = "qm-styles";
function injectLocal() {
  injectUserStyles();
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    .qm-table-card { background:#fff; border:1px solid #e8ecf0; border-radius:10px; overflow:hidden; }
    .qm-table { width:100%; border-collapse:collapse; font-size:13px; }
    .qm-table thead tr { background:#f9fafb; border-bottom:1px solid #e8ecf0; }
    .qm-table th {
      padding:10px 16px; text-align:left;
      font-family:'DM Sans',sans-serif; font-size:9.5px; letter-spacing:.04em;
      color:#9ca3af; white-space:nowrap;
    }
    .qm-table td { padding:13px 16px; vertical-align:middle; border-bottom:1px solid #f3f4f6; }
    .qm-table tbody tr:last-child td { border-bottom:none; }
    .qm-table tbody tr:hover td { background:#fafafa; }
    .qm-project-name { font-size:13.5px; font-weight:500; color:#0c1118; max-width:200px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .qm-project-addr { font-size:11.5px; color:#9ca3af; max-width:200px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .qm-requester-name { font-size:13px; font-weight:500; color:#0c1118; }
    .qm-requester-email { font-size:11.5px; color:#9ca3af; }
    .qm-filter-btn {
      padding:7px 14px; border-radius:7px; font-size:12.5px; font-weight:500;
      cursor:pointer; border:1px solid #e2e8f0; background:#fff;
      color:#6b7280; transition:all 150ms; white-space:nowrap;
    }
    .qm-filter-btn.active { background:#0c1118; color:#fff; border-color:#0c1118; }
    .qm-filter-btn:hover:not(.active) { border-color:#d1d5db; color:#374151; }
    .qm-act-btn {
      display:inline-flex; align-items:center; gap:5px;
      padding:5px 10px; border-radius:6px; font-size:11.5px; font-weight:500;
      cursor:pointer; border:none; transition:background 150ms; white-space:nowrap;
    }
    .qm-act-upload { background:#f4f5f7; color:#374151; }
    .qm-act-upload:hover { background:#e8ecf0; }
    .qm-act-delete { background:rgba(239,68,68,.1); color:#b91c1c; }
    .qm-act-delete:hover { background:rgba(239,68,68,.18); }
    .qm-act-view   { background:#f4f5f7; color:#374151; }
    .qm-act-view:hover { background:#e8ecf0; }
    .qm-toast {
      position:fixed; bottom:24px; right:24px; z-index:60;
      display:flex; align-items:center; gap:10px;
      padding:12px 18px; border-radius:10px;
      font-size:13.5px; font-weight:500; color:#fff;
      box-shadow:0 8px 24px rgba(0,0,0,.2);
      animation:qmToastIn .25s ease;
    }
    .qm-toast-success { background:#0c1118; }
    .qm-toast-error   { background:#dc2626; }
    @keyframes qmToastIn { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
  `;
  document.head.appendChild(el);
}

/* ── Helpers ── */
const BASE = process.env.REACT_APP_BACKEND_URL || "";

async function jfetch(path, opts = {}) {
  const res = await fetch(`${BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    ...opts,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err  = new Error(`${res.status} ${res.statusText}`);
    err.status = res.status;
    err.body   = text;
    throw err;
  }
  return res.json();
}

const money = (x) => Number(x || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

/* ── Quote Management List Page ── */
export function QuoteManagement() {
  useEffect(() => { injectLocal(); }, []);

  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast]   = useState(null);
  const [filter, setFilter] = useState("all");
  const navigate            = useNavigate();
  const { getEditorUrl }    = useEditorLink();

  async function load() {
    setLoading(true);
    try {
      const r = await jfetch(`/api/quotes/org`);
      setItems(r.items || []);
    } catch {
      showToast("Failed to load quotes", "error");
    } finally {
      setLoading(false);
    }
  }

  async function uploadQuoteFile(quoteId, file) {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/${quoteId}/upload`, {
        method: "POST", body: fd, credentials: "include",
      });
      if (!r.ok) throw new Error("Upload failed");
      showToast("Quote uploaded");
      await load();
    } catch {
      showToast("Failed to upload quote", "error");
    }
  }

  async function deleteQuoteFile(quoteId) {
    if (!window.confirm("Delete the uploaded quote file?")) return;
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/${quoteId}/file`, {
        method: "DELETE", credentials: "include",
      });
      if (!r.ok) throw new Error("Delete failed");
      showToast("Quote removed");
      await load();
    } catch {
      showToast("Failed to delete quote", "error");
    }
  }

  useEffect(() => { load().catch(() => {}); }, []);

  function showToast(message, type = "success") {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }

  const filteredItems = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((q) => q.status === filter);
  }, [items, filter]);

  const stats = useMemo(() => {
    const total      = items.length;
    const pending    = items.filter((q) => q.status === "pending_review").length;
    const approved   = items.filter((q) => q.status === "approved").length;
    const totalValue = items.reduce((sum, q) => sum + Number(q.finalTotal || 0), 0);
    return { total, pending, approved, totalValue };
  }, [items]);

  const FILTERS = [
    { value: "all",            label: "All Quotes" },
    { value: "pending_review", label: "Pending Review" },
    { value: "sent_to_client", label: "Sent to Client" },
    { value: "submitted",      label: "Submitted" },
    { value: "approved",       label: "Approved" },
  ];

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "24px" }}>
        <div>
          <div className="up-title">Quote Management</div>
          <div className="up-sub">Review and manage submitted quotes from your team</div>
        </div>
        <button className="up-btn up-btn-outline" onClick={load} disabled={loading}>
          <RefreshCw size={14} style={{ animation: loading ? "upSpin .7s linear infinite" : "none" }} />
          Refresh
        </button>
      </div>

      {/* KPI row */}
      <div className="up-kpi-grid">
        {[
          { accent: "#0c1118", val: stats.total,      lbl: "Total Quotes"    },
          { accent: "#f9ab14", val: stats.pending,    lbl: "Pending Review"  },
          { accent: "#10b981", val: stats.approved,   lbl: "Approved"        },
          { accent: "#6366f1", val: `$${money(stats.totalValue)}`, lbl: "Total Value" },
        ].map((k) => (
          <div className="up-kpi" key={k.lbl}>
            <div className="up-kpi-accent" style={{ background: k.accent }} />
            <div className="up-kpi-val" style={{ fontSize: k.lbl === "Total Value" ? "1.6rem" : undefined }}>
              {loading ? "—" : k.val}
            </div>
            <div className="up-kpi-lbl">{k.lbl}</div>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "20px" }}>
        {FILTERS.map(({ value, label }) => (
          <button key={value} onClick={() => setFilter(value)} className={`qm-filter-btn ${filter === value ? "active" : ""}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="qm-table-card">
        <div style={{ overflowX: "auto" }}>
          <table className="qm-table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Quote Time</th>
                <th>Requesting Member</th>
                <th>Status</th>
                <th>Quote File</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6}><div className="up-loading"><div className="up-spinner" /> Loading…</div></td></tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className="up-empty">
                      <FileText size={36} style={{ color: "#d1d5db", margin: "0 auto" }} />
                      <div className="up-empty-title">No quotes found</div>
                      <div className="up-empty-sub">
                        {filter === "all" ? "No submitted quotes yet." : `No ${filter.replaceAll("_", " ")} quotes.`}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : filteredItems.map((q) => (
                <tr key={q.id}>
                  {/* Project */}
                  <td>
                    <div className="qm-project-name">{q.project?.name || q.projectId}</div>
                    <div className="qm-project-addr">{q?.address || "—"}</div>
                  </td>

                  {/* Time */}
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "12px", color: "#374151" }}>
                      <Calendar size={12} style={{ color: "#9ca3af" }} />
                      {new Date(q.createdAt).toLocaleDateString()}
                    </div>
                    <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "10px", color: "#9ca3af", marginTop: "2px" }}>
                      {new Date(q.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </td>

                  {/* Requester */}
                  <td>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                      <User size={13} style={{ color: "#9ca3af", marginTop: 2, flexShrink: 0 }} />
                      <div>
                        <div className="qm-requester-name">
                          {q.requester ? `${q.requester.firstname} ${q.requester.lastname}` : "Member"}
                        </div>
                        {q.requester?.email && <div className="qm-requester-email">{q.requester.email}</div>}
                        {q.requester?.phone && <div className="qm-requester-email">{q.requester.phone}</div>}
                      </div>
                    </div>
                  </td>

                  {/* Status */}
                  <td>
                    <span className={statusBadgeClass(q.status)}>{fmtStatus(q.status)}</span>
                  </td>

                  {/* Quote file */}
                  <td>
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-start" }}>
                      {q.fileUrl && (
                        <a href={q.fileUrl} target="_blank" rel="noreferrer"
                          style={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "12px", color: "#6366f1", fontWeight: 500, textDecoration: "none" }}>
                          <Download size={12} /> Download Quote
                        </a>
                      )}
                      <label className="qm-act-btn qm-act-upload" style={{ cursor: "pointer" }}>
                        <FileText size={12} />
                        {q.fileUrl ? "Replace" : "Add Quote"}
                        <input type="file" className="hidden" accept=".pdf,.doc,.docx,.csv" style={{ display: "none" }}
                          onChange={(e) => uploadQuoteFile(q.id, e.target.files?.[0])} />
                      </label>
                    </div>
                  </td>

                  {/* Actions */}
                  <td>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "6px" }}>
                      {q.fileUrl && (
                        <button className="qm-act-btn qm-act-delete" onClick={() => deleteQuoteFile(q.id)}>
                          <Trash2 size={12} />
                        </button>
                      )}
                      <FeatureGateButton
                        required={["feat.estimator"]}
                        requireActiveSub={true}
                        variant="black"
                        size="sm"
                        icon={Edit3}
                        onClick={() => navigate(`/dashboard/quotes/${q.projectId}`)}
                      >
                        Quote Editor
                      </FeatureGateButton>
                      <button className="qm-act-btn qm-act-view" onClick={() => window.open(getEditorUrl(q.projectId), "_blank")}>
                        <Eye size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`qm-toast ${toast.type === "success" ? "qm-toast-success" : "qm-toast-error"}`}>
          {toast.type === "success" ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {toast.message}
        </div>
      )}
    </div>
  );
}

export default QuoteManagement;
